import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Car, Timer } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { indexLabels } from "@/i18n";
import { useNavigate } from "react-router-dom";
import { SaleTypeBadge } from "@/components/ui/SaleTypeBadge";
import { useState } from "react";
import { DescriptionPopup } from "@/components/auctions/DescriptionDialog";

interface PublicCampaign {
  id: string;
  name: string;
  description: string;
  thumbnailUrl: string;
  totalListings: number;
  timeLeft: string;
  type: "auction" | "direct-purchase";
  status?: "active" | "upcoming" | "completed" | "cancelled";
  labels?: Array<{
    labelType: "reduced_percentage" | "reduced_amount" | "special";
    text: string;
  }>;
}

interface AuctionGroupsProps {
  groups: PublicCampaign[];
  onGroupClick?: (campaignId: string) => void;
  isAuthenticated?: boolean;
}

const AuctionGroups = ({
  groups,
  onGroupClick,
  isAuthenticated = false,
}: AuctionGroupsProps) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const labels = indexLabels(language);
  const [showDescriptionPopup, setShowDescriptionPopup] = useState<{
    title: string;
    description: string;
  } | null>(null);

  const handleGroupClick = (campaignId: string, status?: string) => {
    // If campaign is upcoming and user is authenticated, don't allow navigation
    if (status === "upcoming" && isAuthenticated) {
      return;
    }

    if (onGroupClick) {
      onGroupClick(campaignId);
    } else {
      navigate(`/campaigns/${campaignId}`);
    }
  };

  const handleDescriptionClick = (e: React.MouseEvent, group: PublicCampaign) => {
    e.stopPropagation();
    setShowDescriptionPopup({
      title: group.name,
      description: group.description,
    });
  };

  // Backend returns Swedish "X dagar" or "Slut" - translate to current language
  const formatTimeLeft = (timeLeft: string): string => {
    if (timeLeft === "Slut") return labels.campaigns.timeLeftEnded;
    const match = timeLeft.match(/^(\d+)\s+dagar$/);
    if (match) return `${match[1]} ${labels.campaigns.timeLeftDays}`;
    return timeLeft;
  };

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-xl md:text-2xl font-bold">{labels.campaigns.title}</h2>
        {labels.campaigns.subtitle && (
          <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">
            {labels.campaigns.subtitle}
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
      {groups.map((group, index) => {
        // Determine if card should be clickable
        const isUpcomingAndAuthenticated =
          group.status === "upcoming" && isAuthenticated;
        const isClickable = !isUpcomingAndAuthenticated;

        return (
          <Card
            key={group.id}
            className={`glass-card overflow-hidden transition-all duration-150 group animate-fade-in ${
              isClickable ? "hover:shadow-lg cursor-pointer" : "cursor-default"
            }`}
            style={{
              animationDelay: `${index * 150}ms`,
            }}
            onClick={() =>
              isClickable && handleGroupClick(group.id, group.status)
            }
          >
            <div className="relative h-48">
              <img
                src={group.thumbnailUrl || "/placeholder.svg"}
                alt={group.name}
                className={`w-full h-full object-cover transition-transform duration-200 ${
                  isClickable ? "group-hover:scale-105" : ""
                }`}
                style={{ imageRendering: "auto" }}
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/40" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3
                  className={`text-xl font-semibold text-white mb-2 transition-colors line-clamp-1 ${
                    isClickable ? "group-hover:text-accent" : ""
                  }`}
                >
                  {group.name}
                </h3>
                <div className="mb-2 relative">
                  <p className="text-sm text-white/80 line-clamp-2 whitespace-pre-wrap">
                    {group.description}
                  </p>
                  {/* Show "visa mer" if description is longer than ~100 chars (roughly 2 lines) */}
                  {group.description && group.description.length > 100 && (
                    <button
                      className="hover:underline font-medium text-blue-400 hover:text-blue-300 mt-1 inline-block"
                      onClick={(e) => handleDescriptionClick(e, group)}
                    >
                      ...{labels.campaigns.showMore}
                    </button>
                  )}
                </div>
                {/* Labels */}
                {group.labels && group.labels.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {group.labels.map((label, index) => {
                      const labelConfig = {
                        reduced_percentage: {
                          color: "bg-red-500/80 text-white",
                          icon: "%",
                        },
                        reduced_amount: {
                          color: "bg-green-500/80 text-white",
                          icon: "kr",
                        },
                        special: {
                          color: "bg-blue-500/80 text-white",
                          icon: "★",
                        },
                      }[label.labelType];

                      return (
                        <Badge
                          key={index}
                          variant="secondary"
                          className={`text-xs px-2 py-0.5 ${labelConfig.color}`}
                        >
                          {labelConfig.icon} {label.text}
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="absolute top-3 right-3 flex flex-col gap-2 items-end">
                {group.status === "upcoming" ? (
                  <Badge className="bg-blue-500/80 text-white flex items-center gap-1">
                    <Timer className="w-3 h-3" />
                    {labels.campaigns.upcoming}
                  </Badge>
                ) : (
                  group.type === "auction" && (
                    <Badge className="bg-yellow-900 text-white">
                      {formatTimeLeft(group.timeLeft)}
                    </Badge>
                  )
                )}
                <SaleTypeBadge
                  saleType={group.type === "auction" ? "auction" : "direct"}
                  className="text-xs"
                />
              </div>
            </div>

            <div className="p-4 bg-black backdrop-blur-sm">
              <div className="flex items-center justify-between text-white/80">
                {group.status !== "upcoming" && (
                  <div className="flex items-center gap-2">
                    <Car className="w-4 h-4" />
                    <span className="text-sm">
                      {labels.campaigns.listingsCount.replace("{count}", String(group.totalListings))}
                    </span>
                  </div>
                )}
                {group.status === "upcoming" && (
                  <div className="flex items-center gap-2 w-full justify-center">
                    <span className="text-sm font-medium text-blue-300">
                      {labels.campaigns.upcomingCampaign}
                    </span>
                  </div>
                )}
                {group.status !== "upcoming" && (
                  <span className="text-sm font-medium text-yellow-400">
                    {labels.campaigns.seeAll}
                  </span>
                )}
              </div>
            </div>
          </Card>
        );
      })}
      
      </div>
      {showDescriptionPopup && (
        <DescriptionPopup
          description={showDescriptionPopup.description}
          title={showDescriptionPopup.title}
          onClose={() => setShowDescriptionPopup(null)}
        />
      )}
    </div>
  );
};

export default AuctionGroups;
